import React, { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ShieldAlert, HeartPulse, Stethoscope, ChevronRight } from 'lucide-react';
import { Button } from './ui/Button';

const About = () => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.about-animate', 
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.1, duration: 0.8, ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
          }
        }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="bg-secondary/30 relative overflow-hidden" id="about">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          <div className="relative aspect-[4/5] sm:aspect-square lg:aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl about-animate order-2 lg:order-1">
             <img 
               src="/images/WhatsApp Image 2026-01-23 at 11.58.30 PM.jpeg" 
               alt="Expert Veterinary Surgery and Clinical Care at Dr. Suresh Pet Care Vanasthalipuram" 
               className="w-full h-full object-cover object-[center_20%] transition-transform duration-[3s] hover:scale-110"
             />
             <div className="absolute inset-0 bg-navy/20 mix-blend-multiply" />
             
             {/* Floating Info Card */}
             <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 bg-white p-6 md:p-8 rounded-[2rem] shadow-2xl max-w-[180px] md:max-w-[240px] border border-slate-100 z-20">
                <div className="flex items-center gap-4 mb-3">
                   <div className="bg-primary/10 p-3 rounded-full">
                      <HeartPulse size={28} className="text-primary" />
                   </div>
                   <h4 className="font-display font-black text-4xl text-navy">23+</h4>
                </div>
                <p className="text-sm font-bold text-navy/80 leading-snug uppercase tracking-wider">Years of Trust & Clinical Excellence</p>
             </div>
          </div>

          <div className="space-y-10 order-1 lg:order-2">
             <div className="about-animate space-y-6">
                <h4 className="text-primary font-bold tracking-[0.2em] uppercase text-[10px] md:text-[11px] flex items-center gap-3">
                   <span className="w-12 h-[2px] bg-primary/30"></span> About Our Hospital
                </h4>
                <h2 className="text-navy font-display font-bold leading-[1.1] text-4xl md:text-5xl lg:text-6xl">
                   Welcome to <br/> <span className="text-primary italic font-normal underline decoration-primary/10 underline-offset-8">Dr. Suresh Pet Care.</span>
                </h2>
                <div className="space-y-4 border-l-4 border-primary/20 pl-8 py-2">
                   <p className="text-luxury font-bold">
                      A leading destination for comprehensive pet healthcare in Vanasthalipuram.
                   </p>
                   <p className="text-muted-foreground text-sm font-medium leading-relaxed">
                      We offer a wide range of veterinary services including health checkups, diagnostics, surgical procedures, emergency care, grooming, and boarding, all under one roof.
                   </p>
                   <p className="text-muted-foreground text-sm font-medium leading-relaxed">
                      Our focus is to ensure your pets receive safe treatment, accurate diagnosis, and compassionate care at every stage.
                   </p>
                </div>
             </div>

             <div className="about-animate grid sm:grid-cols-2 gap-8 pt-4">
                {[
                   { icon: <ShieldAlert size={28} className="text-accent" />, title: "24/7 Protection", desc: "Emergency services available round the clock 365 days." },
                   { icon: <Stethoscope size={28} className="text-primary" />, title: "Expert Care", desc: "Top-tier veterinary physicians and surgeons on board." }
                ].map((item, i) => (
                   <div key={i} className="flex flex-col gap-5 p-6 bg-white/50 rounded-[2rem] border border-white/80 shadow-sm hover:shadow-md transition-all">
                      <div className="bg-white shadow-sm p-4 rounded-full h-fit w-fit border border-slate-50 text-navy">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="font-bold text-navy text-lg mb-2">{item.title}</h4>
                        <p className="text-xs font-medium text-muted-foreground leading-relaxed">{item.desc}</p>
                      </div>
                   </div>
                ))}
             </div>

             <div className="about-animate pt-6">
                <Button className="bg-navy hover:bg-navy/90 text-white font-bold h-16 px-10 rounded-full flex items-center gap-4 shadow-lg hover:-translate-y-1 transition-all">
                   DISCOVER MORE <ChevronRight size={18} />
                </Button>
             </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;
